import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarPlus, Gauge, ShieldAlert, UserPlus } from "lucide-react";
import { api, formatApiError } from "../api";
import { OPERATIONS } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { fadeUp } from "../lib/motion";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { GymLoader } from "../components/common/GymLoader";
import { ErrorState } from "../components/common/ErrorState";
import { SortControl } from "../components/common/SortControl";
import { useSortPersistence } from "../hooks/useSortPersistence";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { FormField, isFieldValid } from "../components/common/FormField";
import { SubmitButton } from "../components/common/SubmitButton";
import { AddMemberToClassDialog } from "../components/scheduling/AddMemberToClassDialog";

const CLASS_SORT_FIELDS = [{ value: "start", label: "Soonest first", type: "date" }, { value: "class_type", label: "Class (A–Z)", type: "alpha" }, { value: "capacity", label: "Capacity", type: "numeric" }];

const TABS = [{ key: "classes", label: "Class schedule" }, { key: "access", label: "Access & leakage" }];

// UX-GAP-01 §1.5: Owner Cockpit "Operations" — was a StubPage.
export default function Operations() {
  const { branches, selectedBranchId } = useAppData();
  const reducedMotion = useReducedMotion();
  const [tab, setTab] = useState("classes");
  const [branchId, setBranchId] = useState(selectedBranchId !== "all" ? selectedBranchId : branches[0]?.id || "");
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => { if (!branchId && branches.length) setBranchId(selectedBranchId !== "all" ? selectedBranchId : branches[0].id); }, [branches, selectedBranchId, branchId]);

  return (
    <div className="space-y-5 max-w-4xl" data-testid={OPERATIONS.page}>
      <motion.div {...fadeUp(reducedMotion)} className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Operations</h1>
          <p className="text-sm mt-0.5" style={{ color: "var(--scarlet-text-secondary)" }}>What's running on the floor, right now.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select onValueChange={setBranchId} value={branchId || undefined}>
            <SelectTrigger data-testid={OPERATIONS.branchSelect} className="w-48"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
            <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
          </Select>
          {tab === "classes" && (
            <Button data-testid={OPERATIONS.addClassButton} onClick={() => setAddOpen(true)} disabled={!branchId} className="text-white gap-2" style={{ background: "var(--scarlet-accent)" }}>
              <CalendarPlus size={15} /> Add class
            </Button>
          )}
        </div>
      </motion.div>

      <div className="flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.key} data-testid={OPERATIONS.tab(t.key)} onClick={() => setTab(t.key)}
            className="text-sm font-medium rounded-[10px] px-3.5 py-1.5 transition-colors"
            style={tab === t.key ? { background: "var(--scarlet-accent)", color: "#fff" } : { background: "transparent", border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text-secondary)" }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "classes" && <ClassScheduleTab branchId={branchId} />}
      {tab === "access" && <AccessLeakageTab branchId={branchId} />}

      <AddClassDialog open={addOpen} onOpenChange={setAddOpen} branchId={branchId} />
    </div>
  );
}

function ClassScheduleTab({ branchId }) {
  const { sortBy, sortDir, setSort } = useSortPersistence("operations-classes", "start", "asc");
  const schedulesQ = useQuery({
    queryKey: ["operations-schedules", branchId, sortBy, sortDir],
    queryFn: async () => (await api.get("/class-schedules", { params: { branch_id: branchId, sort_by: sortBy === "start" ? undefined : sortBy, sort_dir: sortDir } })).data.schedules,
    enabled: !!branchId,
  });
  const staffQ = useQuery({ queryKey: ["staff-list"], queryFn: async () => (await api.get("/staff")).data.staff });
  const nameByStaffId = Object.fromEntries((staffQ.data || []).map((s) => [s.id, s.name]));
  const [addMemberSchedule, setAddMemberSchedule] = useState(null);

  if (schedulesQ.isError) return <ErrorState error={schedulesQ.error} onRetry={() => schedulesQ.refetch()} />;
  if (schedulesQ.isLoading || !schedulesQ.data) return <GymLoader label="Loading classes…" />;
  if (schedulesQ.data.length === 0) return <div className="solid-surface rounded-[14px] p-10 text-center text-sm" data-testid={OPERATIONS.classEmptyState} style={{ color: "var(--scarlet-text-secondary)" }}>No classes scheduled at this branch yet.</div>;

  return (
    <>
      <div className="flex justify-end">
        <SortControl fields={CLASS_SORT_FIELDS} sortBy={sortBy} sortDir={sortDir} onChange={setSort} testId={OPERATIONS.classSortControl} />
      </div>
      <div className="solid-surface rounded-[14px] overflow-hidden">
        <table className="w-full text-sm" data-testid={OPERATIONS.classTable}>
          <thead>
            <tr className="text-left" style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
              <th className="eyebrow px-5 py-3">Class</th>
              <th className="eyebrow px-5 py-3">Trainer</th>
              <th className="eyebrow px-5 py-3">When</th>
              <th className="eyebrow px-5 py-3">Fill</th>
              <th className="eyebrow px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {schedulesQ.data.map((s) => (
              <tr key={s.id} data-testid={OPERATIONS.classRow(s.id)} style={{ borderBottom: "1px solid var(--scarlet-hairline)" }}>
                <td className="px-5 py-3.5 font-medium" style={{ color: "var(--scarlet-text)" }}>{s.class_type} <span className="text-xs font-normal" style={{ color: "var(--scarlet-text-secondary)" }}>({s.recurrence})</span></td>
                <td className="px-5 py-3.5" style={{ color: "var(--scarlet-text-secondary)" }}>{nameByStaffId[s.provider_ref] || s.provider_ref}</td>
                <td className="px-5 py-3.5 text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>{new Date(s.start).toLocaleString()}</td>
                <td className="px-5 py-3.5 tabular-nums-force" style={{ color: "var(--scarlet-text)" }}>{s.confirmed_count}/{s.capacity}</td>
                <td className="px-5 py-3.5 text-right">
                  <Button size="sm" variant="outline" data-testid={OPERATIONS.addMemberButton(s.id)} onClick={() => setAddMemberSchedule(s)} className="gap-1.5">
                    <UserPlus size={13} /> Add member
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddMemberToClassDialog
        open={!!addMemberSchedule} onOpenChange={(v) => !v && setAddMemberSchedule(null)}
        scheduleId={addMemberSchedule?.id} classLabel={addMemberSchedule?.class_type}
      />
    </>
  );
}

function AddClassDialog({ open, onOpenChange, branchId }) {
  const queryClient = useQueryClient();
  const staffQ = useQuery({ queryKey: ["staff-list"], queryFn: async () => (await api.get("/staff")).data.staff, enabled: open });
  const trainers = (staffQ.data || []).filter((s) => s.role_name === "trainer" && s.status === "active");
  const [form, setForm] = useState({ classType: "", providerRef: "", capacity: "15", start: "", end: "" });
  const [error, setError] = useState("");

  useEffect(() => { if (open) { setForm({ classType: "", providerRef: "", capacity: "15", start: "", end: "" }); setError(""); } }, [open]);

  const mutation = useMutation({
    mutationFn: () => api.post("/class-schedules", {
      branch_id: branchId, class_type: form.classType, provider_ref: form.providerRef,
      capacity: Number(form.capacity), start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString(),
    }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["operations-schedules", branchId] }); onOpenChange(false); },
    onError: (e) => setError(formatApiError(e).message),
  });

  const isValid = isFieldValid("text", true, form.classType) && !!form.providerRef && !!form.start && !!form.end;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={OPERATIONS.classFormDialog}>
        <DialogHeader>
          <DialogTitle>Add class</DialogTitle>
          <DialogDescription>Members will be able to book onto this from the Member App.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm shake-on-error" style={{ color: "var(--scarlet-neg)" }} data-testid={OPERATIONS.classFormError}>{error}</p>}
        <div className="space-y-3">
          <FormField label="Class type" required testId={OPERATIONS.classTypeInput} value={form.classType} onChange={(v) => setForm({ ...form, classType: v })} placeholder="e.g. HIIT" />
          <Select onValueChange={(v) => setForm({ ...form, providerRef: v })} value={form.providerRef || undefined}>
            <SelectTrigger data-testid={OPERATIONS.classProviderSelect}><SelectValue placeholder="Pick a trainer" /></SelectTrigger>
            <SelectContent>{trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
          <FormField label="Capacity" required type="number" testId={OPERATIONS.classCapacityInput} value={form.capacity} onChange={(v) => setForm({ ...form, capacity: v })} />
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>Starts</label><input data-testid={OPERATIONS.classStartInput} type="datetime-local" className="w-full mt-1.5 rounded-[10px] px-3 py-2 text-sm" style={{ border: "1px solid var(--scarlet-hairline)" }} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
            <div><label className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>Ends</label><input data-testid={OPERATIONS.classEndInput} type="datetime-local" className="w-full mt-1.5 rounded-[10px] px-3 py-2 text-sm" style={{ border: "1px solid var(--scarlet-hairline)" }} value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
          </div>
        </div>
        <DialogFooter>
          <SubmitButton testId={OPERATIONS.classFormSubmit} disabled={!isValid} pending={mutation.isPending} onClick={() => { setError(""); mutation.mutate(); }} label="Add class" />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// `/occupancy/live` and `/metrics/leakage` are the ONLY 2 real endpoints this schema has for
// "Access & gate log" — both return an aggregate count, never a per-event log — so this tab
// shows exactly those 2 real numbers rather than a fabricated event-by-event table.
function AccessLeakageTab({ branchId }) {
  const occupancyQ = useQuery({ queryKey: ["operations-occupancy", branchId], queryFn: async () => (await api.get("/occupancy/live", { params: { branch_id: branchId } })).data, enabled: !!branchId });
  const leakageQ = useQuery({ queryKey: ["operations-leakage", branchId], queryFn: async () => (await api.get("/metrics/leakage", { params: { branch_id: branchId } })).data, enabled: !!branchId });

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div data-testid={OPERATIONS.occupancyTile} className="solid-surface rounded-[14px] p-5">
        <p className="eyebrow flex items-center gap-1.5"><Gauge size={13} /> Live occupancy</p>
        {occupancyQ.isLoading ? <GymLoader label="…" /> : <p className="text-3xl font-bold mt-1" style={{ color: "var(--scarlet-text)" }}>{occupancyQ.data?.count ?? "—"}</p>}
        <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Members currently checked in.</p>
      </div>
      <div data-testid={OPERATIONS.leakageTile} className="solid-surface rounded-[14px] p-5">
        <p className="eyebrow flex items-center gap-1.5"><ShieldAlert size={13} /> Denied check-ins</p>
        {leakageQ.isLoading ? <GymLoader label="…" /> : <p className="text-3xl font-bold mt-1" style={{ color: leakageQ.data?.denied_count > 0 ? "var(--scarlet-neg)" : "var(--scarlet-text)" }}>{leakageQ.data?.denied_count ?? "—"}</p>}
        <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Check-ins blocked at the gate (expired plan, dues, etc). No per-event log exists yet — this is a running total.</p>
      </div>
    </div>
  );
}
